import { useState, useEffect } from "react";
import { usePlayerStore, formatTime } from "@/lib/player";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Volume2, VolumeX, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/ThemeProvider";
import { motion } from "framer-motion";
// queue and top-right menu removed
import { Badge } from "@/components/ui/badge";
import { AudioWaveform } from "./AudioWaveform";
import { HolographicAlbumCover } from "./HolographicAlbumCover";
import { useBackendSignedUrl } from "@/hooks/useBackendSignedUrl";
import { getTrackImageUrl } from "@/lib/image-utils";
// import { useTracksStore } from "@/lib/tracks";

export function ExpandedPlayer({
  audioRef,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
}) {
  // audioRef is now passed as a prop
  const {
    currentTrack,
    isPlaying,
    position,
    duration,
    volume,
    isMuted,
    pauseTrack,
    resumeTrack,
    setPosition,
    setVolume,
    toggleMute,
    toggleExpanded,
    stopCurrentTrack,
  } = usePlayerStore();

  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  // Queue UI removed
  const [isLiked, setIsLiked] = useState(false);
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  // Get signed URL from backend using track ID (lazy loading - only fetch when needed).
  // Thread the track kind so reference tracks route to the reference-audio bucket (C1).
  const {
    signedUrl: signedAudioUrl,
    loading: urlLoading,
    error: urlError,
    fetchSignedUrl,
  } = useBackendSignedUrl(
    currentTrack?.id != null ? String(currentTrack.id) : null,
    false, // Disable auto-fetch
    currentTrack?.kind ? { kind: currentTrack.kind } : undefined
  );

  // Track the previous track ID to detect actual changes
  const [previousTrackId, setPreviousTrackId] = useState<
    string | number | null
  >(null);

  // Only stop current track when it's actually a different track
  useEffect(() => {
    if (
      audioRef.current &&
      currentTrack &&
      currentTrack.id !== previousTrackId
    ) {
      // Only stop if this is actually a new track (not just switching between players)
      const isNewTrack =
        previousTrackId !== null && currentTrack.id !== previousTrackId;

      if (isNewTrack) {
        if (!audioRef.current.ended) {
          // User-initiated track change: stop current audio aggressively to prevent overlap
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.muted = true;
          audioRef.current.volume = 0;
          stopCurrentTrack();
        }
        // Auto-advance (audio.ended): skip aggressive stop — audio already finished,
        // isPlaying stays true so signed URL fetch and playback proceed naturally
        setPreviousTrackId(currentTrack.id);
      } else {
        // Just update the track ID without stopping audio
        setPreviousTrackId(currentTrack.id);
      }
    }
  }, [currentTrack?.id, previousTrackId, stopCurrentTrack]);

  // Load new track and reset audio only if track changes AND we have signed URL
  useEffect(() => {
    if (audioRef.current && currentTrack && signedAudioUrl) {
      const audioUrl = signedAudioUrl;

      if (audioRef.current.src !== audioUrl) {
        // Store current playback state before changing source
        const wasPlaying = !audioRef.current.paused;
        const currentTime = audioRef.current.currentTime;

        audioRef.current.src = audioUrl;
        audioRef.current.load();

        // Restore volume and unmute for new track
        audioRef.current.muted = false;
        audioRef.current.volume = 1;

        // Only reset position for actual new tracks, not when switching between players
        // The position should be preserved from the player store
        setCurrentTime(0);
        setAudioDuration(0);

        // Only play if isPlaying is true
        if (isPlaying) {
          audioRef.current.play().catch(() => {
            // Play failed, waiting for canplay event
          });
        }
      } else {
        // If audio is already loaded, set duration immediately
        if (
          !isNaN(audioRef.current.duration) &&
          audioRef.current.duration > 0
        ) {
          setAudioDuration(audioRef.current.duration);
        }
      }
    }
  }, [currentTrack?.id, signedAudioUrl, isPlaying]); // Only load when we have signed URL

  // Play/pause audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      // Fetch signed URL if not already available when play is requested
      if (!signedAudioUrl && !urlLoading && !urlError) {
        fetchSignedUrl();
        return; // Wait for signed URL to be fetched
      }

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Only handle non-abort errors to avoid spam
          if (error.name !== "AbortError") {
            pauseTrack();
          }
        });
      }
    } else {
      audio.pause();
    }
  }, [isPlaying, signedAudioUrl, urlLoading, urlError, fetchSignedUrl]);

  // Audio events and sync volume/mute
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Sync volume and mute
    audio.volume = volume;
    audio.muted = isMuted;
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setPosition(duration ? (audio.currentTime / duration) * 100 : 0);
    };
    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration);
    };
    const handleCanPlay = () => {
      // Auto-play when audio is ready and we have a current track
      if (currentTrack && !isPlaying) {
        resumeTrack();
      }
    };

    const handleCanPlayThrough = () => {
      // Also try to play on canplaythrough for faster response
      if (currentTrack && !isPlaying) {
        resumeTrack();
      }
    };

    const handleEnded = () => {
      const state = usePlayerStore.getState();
      if (state.repeatMode === "one") {
        // Repeat current track
        audio.currentTime = 0;
        audio.play().catch(() => {});
      } else if (state.queue.length > 0 || (state.repeatMode === "all" && state.history.length > 0)) {
        // Advance to next track in queue (or loop via history for repeat-all)
        state.nextTrack();
      } else {
        // Nothing left to play
        pauseTrack();
        audio.currentTime = 0;
        setPosition(0);
      }
    };
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [
    setPosition,
    volume,
    isMuted,
    currentTrack,
    isPlaying,
    resumeTrack,
    duration,
    pauseTrack,
  ]);

  // Note: Position reset is now handled in the player store's playTrack function
  // to avoid losing progress when switching between mini and expanded players

  // Sync audio currentTime with position when switching between players
  useEffect(() => {
    if (audioRef.current && duration > 0 && position > 0) {
      const targetTime = (position / 100) * duration;
      if (Math.abs(audioRef.current.currentTime - targetTime) > 1) {
        audioRef.current.currentTime = targetTime;
      }
    }
  }, [position, duration]);

  // Previous/Next controls removed

  if (!currentTrack) return null;

  return (
    <motion.div
      exit={{ opacity: 0, scale: 0.98, y: 40 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      style={{ "--zen-enter-ms": "400ms" } as React.CSSProperties}
      className={cn(
        "zen-enter-up fixed inset-0 z-50",
        isDarkMode
          ? "bg-background/95 backdrop-blur-md"
          : "bg-white/95 backdrop-blur-md"
      )}
    >
      <div className="container mx-auto h-full flex flex-col p-6">
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              isDarkMode
                ? "text-white hover:bg-zinc-800"
                : "text-zinc-700 hover:bg-zinc-100"
            )}
            onClick={toggleExpanded}
            aria-label="Close player"
          >
            <X className="h-5 w-5" />
          </Button>
          {/* Top-right options removed */}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <HolographicAlbumCover
              imageUrl={getTrackImageUrl(currentTrack, 400)}
              isPlaying={isPlaying}
              size="lg"
            />
          </div>

          <div className="w-full max-w-md text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <h2
                className={cn(
                  "text-2xl font-bold",
                  isDarkMode ? "text-white" : "text-zinc-900"
                )}
              >
                {currentTrack.title}
              </h2>
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                {currentTrack.genre}
              </Badge>
            </div>
            <p
              className={cn(
                "text-lg",
                isDarkMode ? "text-zinc-400" : "text-zinc-600"
              )}
            >
              {currentTrack.artist}
            </p>
          </div>

          <div className="w-full max-w-md mb-8">
            <div className="mb-4">
              <AudioWaveform
                isPlaying={isPlaying}
                progress={position}
                onSeek={setPosition}
                className="h-16"
                trackId={
                  typeof currentTrack.id === "number"
                    ? currentTrack.id
                    : undefined
                }
              />
            </div>

            <div className="flex items-center gap-4 mb-2">
              <span
                className={cn(
                  "text-sm",
                  isDarkMode ? "text-zinc-400" : "text-zinc-600"
                )}
              >
                {formatTime(currentTime)}
              </span>
              <Slider
                variant="theme-aware"
                aria-label="Seek"
                value={[duration ? (currentTime / duration) * 100 : 0]}
                max={100}
                step={0.1}
                onValueChange={(values) => {
                  if (audioRef.current && duration) {
                    audioRef.current.currentTime = (values[0] / 100) * duration;
                  }
                }}
                className="flex-1"
              />
              <span
                className={cn(
                  "text-sm",
                  isDarkMode ? "text-zinc-400" : "text-zinc-600"
                )}
              >
                {formatTime(duration || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Previous button removed */}
                <Button
                  variant={isDarkMode ? "secondary" : "default"}
                  size="icon"
                  className={cn(
                    "h-10 w-10 rounded-full",
                    !isDarkMode && "bg-zinc-900 hover:bg-zinc-800 text-white"
                  )}
                  onClick={() => {
                    if (isPlaying) {
                      pauseTrack();
                      if (audioRef.current) audioRef.current.pause();
                    } else {
                      resumeTrack();
                      if (audioRef.current) audioRef.current.play();
                    }
                  }}
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </Button>
                {/* Next/Repeat removed */}
              </div>

              <div className="flex items-center gap-2">
                {/* Queue button removed */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-10 w-10",
                      isDarkMode
                        ? "text-white hover:bg-zinc-800"
                        : "text-zinc-700 hover:bg-zinc-100"
                    )}
                    onClick={toggleMute}
                    aria-label={isMuted || volume === 0 ? "Unmute" : "Mute"}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="h-5 w-5" />
                    ) : (
                      <Volume2 className="h-5 w-5" />
                    )}
                  </Button>
                  <div className="w-24">
                    <Slider
                      variant="theme-aware"
                      aria-label="Volume"
                      value={[isMuted ? 0 : volume]}
                      max={1}
                      step={0.01}
                      onValueChange={(values) => setVolume(values[0])}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mb-4">
          <Button
            variant="outline"
            size="sm"
            className={cn(
              isDarkMode
                ? "text-white hover:bg-zinc-800"
                : "text-zinc-700 hover:bg-zinc-100"
            )}
            onClick={toggleExpanded}
          >
            <Minimize2 className="h-4 w-4 mr-2" />
            Minimize Player
          </Button>
        </div>
      </div>

      {/* Queue panel removed */}
    </motion.div>
  );
}
